import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Copy, RefreshCw, Power, KeyRound, Sparkles, ShieldCheck, Check } from 'lucide-react';

export default function ClaudeConnector() {
  const { token, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newToken, setNewToken] = useState('');
  const [copied, setCopied] = useState('');

  const mcpUrl = (typeof window !== 'undefined' ? window.location.origin : '') + '/api/mcp';
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/integrations/claude/connect`, authHeaders);
      setStatus(res.data);
    } catch (e) {
      toast.error('Could not load connector status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); /* eslint-disable-next-line */ }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/integrations/claude/connect`, {}, authHeaders);
      setNewToken(res.data.token);
      toast.success('Connector key generated — copy it now, it is only shown once.');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Disconnect Claude? Your existing key will stop working immediately.')) return;
    try {
      await axios.delete(`${API}/integrations/claude/connect`, authHeaders);
      setNewToken('');
      toast.success('Disconnected. Claude can no longer access Memorial Desk.');
      fetchStatus();
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy — select the text manually');
    }
  };

  const CopyRow = ({ label, value, testid, copyKey }) => (
    <div className="space-y-1" data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-slate-900 text-emerald-300 text-sm rounded-md px-3 py-2 overflow-x-auto whitespace-nowrap">{value}</code>
        <Button size="sm" variant="outline" onClick={() => copy(value, copyKey)} data-testid={`copy-${copyKey}`}>
          {copied === copyKey ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4" data-testid="claude-connector-page">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/settings')} className="mb-4" data-testid="back-to-settings">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Settings
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Connect to Claude</h1>
            <p className="text-sm text-slate-500">Let your Claude app create and amend orders in Memorial Desk on your command.</p>
          </div>
          {status?.connected && !loading && (
            <Badge className="ml-auto bg-green-100 text-green-700 border-green-200" data-testid="connected-badge">Connected</Badge>
          )}
        </div>

        {/* Step 1: generate key */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg"><KeyRound className="w-5 h-5 text-slate-600" /> Step 1 — Your connector key</CardTitle>
            <CardDescription>This secret key lets Claude act on your account. Treat it like a password.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="text-slate-500 text-sm">Loading…</div>
            ) : (
              <>
                {newToken ? (
                  <div className="space-y-3">
                    <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                      Copy this now — for security it won't be shown again. If you lose it, just generate a new one.
                    </div>
                    <CopyRow label="Connector key" value={newToken} testid="new-token-row" copyKey="token" />
                  </div>
                ) : status?.connected ? (
                  <div className="text-sm text-slate-600">
                    A key is already set up (<code className="bg-slate-100 px-1.5 py-0.5 rounded">{status.token_preview}</code>
                    {status.created_at ? `, created ${new Date(status.created_at).toLocaleDateString()}` : ''}).
                    {status.last_used_at ? ` Last used ${new Date(status.last_used_at).toLocaleString()}.` : ' Not used yet.'}
                    <div className="mt-1 text-slate-500">The full key is hidden. Generate a new one if you need to re-copy it (the old one stops working).</div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-600">No key yet. Generate one to connect Claude.</div>
                )}

                <div className="flex gap-2">
                  <Button onClick={generate} disabled={generating} className="bg-orange-600 hover:bg-orange-700" data-testid="generate-key-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                    {status?.connected ? 'Generate New Key' : 'Generate Key'}
                  </Button>
                  {status?.connected && (
                    <Button variant="outline" onClick={revoke} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="revoke-key-btn">
                      <Power className="w-4 h-4 mr-2" /> Disconnect
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Step 2: connector URL */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Step 2 — The connector address</CardTitle>
            <CardDescription>You'll paste this URL into Claude.</CardDescription>
          </CardHeader>
          <CardContent>
            <CopyRow label="Remote MCP URL" value={mcpUrl} testid="mcp-url-row" copyKey="url" />
          </CardContent>
        </Card>

        {/* Step 3: set up in Claude */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Step 3 — Add it in Claude (one-time, ~2 minutes)</CardTitle>
            <CardDescription>Claude Pro, Max, Team or Enterprise required for custom connectors.</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
              <li>In Claude (desktop or claude.ai), open <strong>Settings → Connectors</strong>.</li>
              <li>Click <strong>Add custom connector</strong> → choose <strong>Custom</strong> / <strong>Web</strong>.</li>
              <li>Paste the <strong>Remote MCP URL</strong> from Step 2.</li>
              <li>Under <strong>Authentication</strong> choose <strong>No sign-in</strong>, then open <strong>Request headers</strong> and add:
                <div className="mt-2 bg-slate-900 text-emerald-300 text-sm rounded-md px-3 py-2 flex items-center gap-2">
                  <code className="flex-1 overflow-x-auto whitespace-nowrap">Authorization: Bearer &lt;your key from Step 1&gt;</code>
                  <Button size="sm" variant="outline" onClick={() => copy('Authorization', 'hdrname')}>
                    {copied === 'hdrname' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="text-xs text-slate-500 mt-1">Header name is <code>Authorization</code>; value is the word <code>Bearer</code>, a space, then your key.</div>
              </li>
              <li>Save, then click <strong>Connect</strong>. Memorial Desk will appear as a connector with tools.</li>
              <li>Now just talk to Claude, e.g. upload a drawing and say: <em>"Create this as a new memorial order in Memorial Desk for the Smith family at St Mary's."</em></li>
            </ol>

            <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase mb-2">What Claude can do</div>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Create a new <strong>memorial order</strong> (stone, inscription, cemetery, price…)</li>
                <li>• Create a new <strong>worktop order/quote</strong> with pieces, edges &amp; cut-outs (we calculate the price)</li>
                <li>• <strong>Amend</strong> an existing memorial order (inscription, stone, price, status)</li>
                <li>• <strong>Search &amp; look up</strong> your orders</li>
              </ul>
            </div>

            <div className="mt-4 flex items-start gap-2 text-sm text-slate-600">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
              <span>Changes are applied immediately and only to <strong>your</strong> company's data. Disconnect any time in Step 1 to instantly cut Claude's access.</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
